import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import FilterBar from './components/FilterBar';
import CasinoGrid from './components/CasinoGrid';
import CasinoModal from './components/CasinoModal';
import AgeVerificationModal from './components/AgeVerificationModal';
import HeroCard from './components/HeroCard';
import { fetchCasinos, fetchCasinoBySlug, fetchGeneralSettings, fetchBranding } from './services/api';

const AGE_VERIFICATION_KEY = 'ageVerified';

// On /bonus/<kuerzel>/ WordPress renders the root node with the offer slug.
// In that case the app shows only the detail modal of that single offer.
const rootElement = typeof document !== 'undefined' ? document.getElementById('root') : null;
const OFFER_SLUG = (rootElement && rootElement.dataset.offerSlug) || '';
const HOME_URL = (rootElement && rootElement.dataset.homeUrl) || '/';
const isSingleOfferPage = Boolean(OFFER_SLUG);

function App() {
  const [casinos, setCasinos] = useState([]);
  const [filteredCasinos, setFilteredCasinos] = useState([]);
  const [selectedCasino, setSelectedCasino] = useState(null);
  const [offerNotFound, setOfferNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [showRatingsAndProcons, setShowRatingsAndProcons] = useState(true);
  const [brandingLogo, setBrandingLogo] = useState('https://mobonus.com/wp-content/uploads/2025/11/Logo2.png');
  const [backgroundLogo, setBackgroundLogo] = useState('https://mobonus.com/wp-content/uploads/2025/11/Logo2.png');
  const [promotionImage, setPromotionImage] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    bonusType: 'all',
    minBonus: 0,
    sortBy: 'placement'
  });

  useEffect(() => {
    // Check if age verification was already done in this session
    const ageVerified = sessionStorage.getItem(AGE_VERIFICATION_KEY);
    if (!ageVerified) {
      setShowAgeVerification(true);
    } else {
      loadData();
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load settings and casinos in parallel
      await Promise.all([
        loadSettings(),
        isSingleOfferPage ? loadSingleOffer() : loadCasinos()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSingleOffer = async () => {
    try {
      const casino = await fetchCasinoBySlug(OFFER_SLUG);
      setSelectedCasino(casino);
    } catch (error) {
      console.error('Error loading offer:', error);
      setOfferNotFound(true);
    }
  };

  const goToOverview = () => {
    window.location.href = HOME_URL;
  };

  const loadSettings = async () => {
    try {
      const settings = await fetchGeneralSettings();
      setShowRatingsAndProcons(settings.showRatingsAndProcons);

      // Set promotion image settings
      if (settings.promotionImage && settings.promotionImage.enabled) {
        setPromotionImage(settings.promotionImage);
      }

      const branding = await fetchBranding();
      setBrandingLogo(branding.logoUrl);
      setBackgroundLogo(branding.backgroundLogo || branding.logoUrl);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [casinos, filters]);

  const loadCasinos = async () => {
    try {
      const data = await fetchCasinos();
      setCasinos(data);
    } catch (error) {
      console.error('Error loading casinos:', error);
    }
  };

  const applyFilters = () => {
    let result = [...casinos];

    // Search filter
    if (filters.search) {
      result = result.filter(casino =>
        casino.name.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Bonus type filter
    if (filters.bonusType !== 'all') {
      result = result.filter(casino =>
        casino.bonusType === filters.bonusType
      );
    }

    // Minimum bonus filter
    if (filters.minBonus > 0) {
      result = result.filter(casino =>
        casino.bonusAmount >= filters.minBonus
      );
    }

    // Sorting
    switch (filters.sortBy) {
      case 'placement':
        result.sort((a, b) => (a.placement || 999) - (b.placement || 999));
        break;
      case 'bonus':
        result.sort((a, b) => b.bonusAmount - a.bonusAmount);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break;
    }

    setFilteredCasinos(result);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleAgeConfirm = () => {
    sessionStorage.setItem(AGE_VERIFICATION_KEY, 'true');
    setShowAgeVerification(false);
    loadData();
  };

  const handleAgeDecline = () => {
    window.location.href = 'https://www.google.com';
  };

  // /bonus/<kuerzel>/ – only the detail modal of a single offer
  if (isSingleOfferPage) {
    return (
      <div className="App single-offer-page">
        <AgeVerificationModal
          isOpen={showAgeVerification}
          onConfirm={handleAgeConfirm}
          onDecline={handleAgeDecline}
        />
        <div className="background-logo" style={{ backgroundImage: `url(${backgroundLogo})` }}></div>

        {!showAgeVerification && loading && (
          <div className="single-offer-status">
            <img src={brandingLogo} alt="" className="single-offer-spinner" />
          </div>
        )}

        {offerNotFound && (
          <div className="single-offer-status">
            <h2>Angebot nicht gefunden</h2>
            <p>Dieses Angebot ist nicht mehr verfügbar.</p>
            <button className="single-offer-back" onClick={goToOverview}>
              Alle Angebote ansehen
            </button>
          </div>
        )}

        {selectedCasino && (
          <CasinoModal
            casino={selectedCasino}
            onClose={goToOverview}
            showRatingsAndProcons={showRatingsAndProcons}
            standalone
          />
        )}
      </div>
    );
  }

  return (
    <div className="App">
      <AgeVerificationModal
        isOpen={showAgeVerification}
        onConfirm={handleAgeConfirm}
        onDecline={handleAgeDecline}
      />
      <div className="background-logo" style={{ backgroundImage: `url(${backgroundLogo})` }}></div>
      <Header />
      <FilterBar filters={filters} onFilterChange={handleFilterChange} />
      {promotionImage && promotionImage.imageUrl && (
        <div className="promotion-image-container" style={{
          display: 'flex',
          justifyContent: promotionImage.position,
          paddingTop: promotionImage.paddingTop,
          paddingBottom: promotionImage.paddingBottom,
          paddingLeft: promotionImage.paddingLeft,
          paddingRight: promotionImage.paddingRight,
        }}>
          {promotionImage.link ? (
            <a
              href={promotionImage.link}
              target={promotionImage.openNewTab ? '_blank' : '_self'}
              rel={promotionImage.openNewTab ? 'noopener noreferrer' : undefined}
              style={{
                display: 'block',
                width: promotionImage.width,
                maxWidth: promotionImage.maxWidth,
              }}
            >
              <img
                src={promotionImage.imageUrl}
                alt="Promotion"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: promotionImage.borderRadius,
                  display: 'block',
                }}
              />
            </a>
          ) : (
            <img
              src={promotionImage.imageUrl}
              alt="Promotion"
              style={{
                width: promotionImage.width,
                maxWidth: promotionImage.maxWidth,
                height: 'auto',
                borderRadius: promotionImage.borderRadius,
                display: 'block',
              }}
            />
          )}
        </div>
      )}
      {filteredCasinos.length > 0 && filteredCasinos[0].isHero && (
        <div className="hero-container">
          <HeroCard
            casino={filteredCasinos[0]}
            onClick={() => setSelectedCasino(filteredCasinos[0])}
          />
        </div>
      )}
      <CasinoGrid
        casinos={filteredCasinos}
        loading={loading}
        onCasinoClick={setSelectedCasino}
        showRatingsAndProcons={showRatingsAndProcons}
        brandingLogo={brandingLogo}
      />
      {selectedCasino && (
        <CasinoModal
          casino={selectedCasino}
          onClose={() => setSelectedCasino(null)}
          showRatingsAndProcons={showRatingsAndProcons}
        />
      )}
    </div>
  );
}

export default App;
